import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import { createTemplate, listTemplates } from "@/api/client";
import { useSession } from "@/auth/session";
import { formatDate } from "@/components/format";
import { EmptyState, ErrorBanner, StatusBadge, ToolbarButton, WorkspaceHeader } from "@/components/ui";
import { ScrollArea } from "@/components/ScrollArea";

export function TemplateLibrary() {
  const { can } = useSession();
  const templates = useQuery({ queryKey: ["templates"], queryFn: listTemplates });
  const items = templates.data ?? [];

  return (
    <section className="space-y-5">
      <WorkspaceHeader title="Templates" detail="Certificate layouts; each program issues with its template's current version" />
      <ErrorBanner error={templates.error} />
      {can("template.manage") ? <CreateTemplateForm /> : null}

      <div className="overflow-hidden rounded-lg border border-zinc-200 bg-white">
        {templates.isLoading ? (
          <EmptyState title="Loading templates" detail="Fetching the template library." />
        ) : templates.isSuccess && items.length === 0 ? (
          <EmptyState title="No templates yet" detail="Create a template, then add and publish its first version." />
        ) : (
          <ScrollArea label="Templates">
            <table className="w-full min-w-[720px] text-left text-sm">
              <thead className="border-b border-zinc-200 bg-zinc-50 text-xs uppercase text-zinc-500">
                <tr>
                  <th className="px-4 py-3 font-medium">Template</th>
                  <th className="px-4 py-3 font-medium">Certificate type</th>
                  <th className="px-4 py-3 font-medium">Current version</th>
                  <th className="px-4 py-3 font-medium">Published</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-100">
                {items.map((template) => (
                  <tr key={template.id} className="hover:bg-zinc-50">
                    <td className="px-4 py-3">
                      <Link
                        to="/templates/$templateId"
                        params={{ templateId: template.id }}
                        className="font-medium text-zinc-950 underline-offset-2 hover:underline"
                      >
                        {template.name}
                      </Link>
                    </td>
                    <td className="px-4 py-3 font-mono text-xs text-zinc-600">{template.cert_type}</td>
                    <td className="px-4 py-3">
                      {template.current_version ? (
                        <span className="inline-flex items-center gap-2">
                          <span className="font-medium">v{template.current_version.version_no}</span>
                          <StatusBadge status={template.current_version.status} />
                        </span>
                      ) : (
                        <span className="text-zinc-500">No published version</span>
                      )}
                    </td>
                    {/* The list carries no updated time; the current version's publish time stands in. */}
                    <td className="px-4 py-3 text-zinc-500">{formatDate(template.current_version?.effective_from ?? undefined)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </ScrollArea>
        )}
      </div>
    </section>
  );
}

function CreateTemplateForm() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [certType, setCertType] = useState("");
  const ready = name.trim() !== "" && certType.trim() !== "";

  const create = useMutation({
    mutationFn: () => createTemplate({ name: name.trim(), cert_type: certType.trim() }),
    onSuccess: async (created) => {
      await queryClient.invalidateQueries({ queryKey: ["templates"] });
      // A new template has no versions; its page is where the first draft is made.
      await navigate({ to: "/templates/$templateId", params: { templateId: created.id } });
    },
  });

  const field = "mt-1 w-full rounded-md border border-zinc-300 bg-white px-3 py-2 text-sm outline-none focus:border-brand focus:ring-1 focus:ring-brand";
  return (
    <div className="space-y-3 rounded-lg border border-zinc-200 bg-white p-4">
      <h2 className="text-sm font-semibold">New template</h2>
      <div className="grid gap-3 md:grid-cols-[minmax(0,2fr)_minmax(0,1fr)_auto] md:items-end">
        <label className="block text-sm">
          <span className="font-medium text-zinc-700">Template name</span>
          <input value={name} onChange={(e) => setName(e.target.value)} className={field} />
        </label>
        <label className="block text-sm">
          <span className="font-medium text-zinc-700">Certificate type</span>
          <input value={certType} onChange={(e) => setCertType(e.target.value)} placeholder="e.g. associate" className={field} />
        </label>
        <ToolbarButton tone="primary" disabled={!ready || create.isPending} onClick={() => create.mutate()}>
          {create.isPending ? "Creating..." : "Create template"}
        </ToolbarButton>
      </div>
      <ErrorBanner error={create.error} />
    </div>
  );
}
